import apiUrl from "slices/apiEndpoint";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Nav, Placeholder, Row, Spinner, Table } from "react-bootstrap";
import ApplicationDetailModal from "./ApplicationDetailModal";
import SicCodeSearchField from "./SicCodeSearchField";
import { AlertCircle, Building2, CheckCircle2, Clock3, CreditCard, FileSearch, FileText, RefreshCw, Send, ShieldCheck, Trash2, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CallAPIDeletePromise, CallAPIGetPromise, CallAPIPostPromise } from "Common/commaon";
import api from "Common/api";

type MerchantPageMode =
  | "business-info"
  | "owners"
  | "kyc"
  | "status"
  | "payment-setup"
  | "payments"
  | "payouts";

type Props = {
  mode: MerchantPageMode;
};

const routeTabs: Array<{ key: MerchantPageMode; label: string; path: string }> = [
  { key: "business-info", label: "Business", path: "/merchant/business-info" },
  { key: "owners", label: "Owners", path: "/merchant/owners" },
  { key: "kyc", label: "KYC", path: "/merchant/kyc" },
  { key: "status", label: "Status", path: "/merchant/status" },
  { key: "payment-setup", label: "Payment Setup", path: "/merchant/payment-setup" },
  { key: "payments", label: "Payments", path: "/merchant/payments" },
  { key: "payouts", label: "Payouts", path: "/merchant/payouts" },
];

const defaultBusinessProfile = {
  dbaName: "",
  legalName: "",
  phoneNumber: "",
  corporateEmail: "",
  businessAddress: {
    address: "",
    city: "",
    state: "",
    zipCode: "",
  },
  ownershipType: "Sole Proprietor",
  yearsOwned: "",
  websiteUrl: "",
  numberOfLocation: 1,
  federalTaxId: "",
  productServiceSold: "",
  sicCode: "",
  activeMonths: ["Jan", "Feb", "Mar"],
};

const defaultTransactionInfo = {
  largestTransactionAmount: 0,
  monthlySalesVolume: 0,
};

const defaultOwner = {
  type: "Owner 1",
  title: "",
  firstName: "",
  middleName: "",
  lastName: "",
  residenceAddress: {
    address: "",
    city: "",
    state: "",
    zipCode: "",
  },
  dateOfBirth: "",
  ownershipPercentage: 100,
  ssn: "",
};

const MERCHANT_DRAFT_KEY = "deluxeMerchantDraft";

function readMerchantDraft() {
  try {
    const raw = localStorage.getItem(MERCHANT_DRAFT_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_error) {
    return {};
  }
}
const PHONE_PATTERN = /^(\+\d{1,3}[\s-]?)?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP_PATTERN = /^\d{5}(?:-\d{4})?$/;
const EIN_PATTERN = /^\d{2}-\d{7}$/;
const SSN_PATTERN = /^\d{3}-\d{2}-\d{4}$/;
const URL_PATTERN = /^https?:\/\/[^\s]+\.[^\s]+$/i;
const BANK_ACCOUNT_PATTERN = /^\d{8,17}$/;
const OWNERSHIP_TYPES = [
  "Sole Proprietor",
  "Limited Liability Co.",
  "Partnership",
  "Not for Profit",
  "PA or PC",
  "Corporation",
  "City/Government",
];

const APPLICATION_STATUS_STEPS = [
  { key: "IN_PROGRESS", label: "In Progress", description: "Application data is still being collected." },
  { key: "REVIEW_FOR_SUBMISSION", label: "Review", description: "Ready for final partner review." },
  { key: "MERCHANT_APPROVAL_REQUIRED", label: "Merchant Approval", description: "Contractor must accept merchant terms." },
  { key: "SUBMITTED", label: "Submitted", description: "Deluxe is reviewing the application." },
  { key: "ACCEPTED", label: "Accepted", description: "Application has been accepted by Deluxe." },
  { key: "ACTIVE", label: "Active", description: "Merchant account is ready for payments." },
];

function isValidAbaRoutingNumber(value = "") {
  const digits = String(value).replace(/\D/g, "");
  if (!/^\d{9}$/.test(digits) || /^0{9}$/.test(digits)) return false;

  const numbers = digits.split("").map(Number);
  const checksum =
    3 * (numbers[0] + numbers[3] + numbers[6]) +
    7 * (numbers[1] + numbers[4] + numbers[7]) +
    (numbers[2] + numbers[5] + numbers[8]);

  return checksum % 10 === 0;
}

function isValidBankAccountNumber(value = "") {
  return BANK_ACCOUNT_PATTERN.test(String(value));
}

function getBadgeVariant(value = "") {
  const normalized = String(value).toUpperCase();
  if (normalized === "ACTIVE" || normalized === "ACCEPTED") return "success";
  if (normalized === "REJECTED") return "danger";
  if (normalized === "SUBMITTED" || normalized === "PENDING") return "info";
  if (normalized.includes("REVIEW") || normalized.includes("APPROVAL")) return "warning";
  return "secondary";
}

function decodeHtmlEntities(value = "") {
  if (typeof window === "undefined") return value;

  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

function sanitizeAgreementHtml(value = "") {
  if (typeof window === "undefined") return value;

  const decoded = decodeHtmlEntities(String(value || ""));
  const documentFragment = new DOMParser().parseFromString(decoded, "text/html");

  documentFragment
    .querySelectorAll("script, style, iframe, object, embed, link, meta")
    .forEach((element) => element.remove());

  documentFragment.querySelectorAll("*").forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const attributeValue = attribute.value.trim().toLowerCase();

      if (
        name.startsWith("on") ||
        ((name === "href" || name === "src") && attributeValue.startsWith("javascript:"))
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return documentFragment.body.innerHTML;
}

function formatConsentTitle(propertyName = "") {
  return String(propertyName)
    .replace(/^accept/i, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim() || "Merchant acknowledgement";
}

function getTradesmensStatus(deluxeStatus: any) {
  const apiStatus = deluxeStatus?.applicationStatus;
  const merchantStatus = deluxeStatus?.merchantStatus;
  if (merchantStatus === "ACTIVE") return "active";
  if (apiStatus === "REJECTED") return "rejected";
  if (apiStatus === "ACCEPTED") return "accepted";
  if (apiStatus === "SUBMITTED") return "under_review";
  if (apiStatus === "MERCHANT_APPROVAL_REQUIRED") return "approval_required";
  if (apiStatus === "REVIEW_FOR_SUBMISSION") return "review";
  return "in_progress";
}

function formatUsPhone(value = "") {
  const digits = String(value).replace(/\D/g, "");
  const normalized = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (normalized.length !== 10) return value;
  return `${normalized.slice(0, 3)}-${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

/** Deluxe KYC OTP: 10-digit numeric string (e.g. 8007031111). */
function formatDeluxePhone(value = "") {
  const digits = String(value).replace(/\D/g, "");
  const normalized = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return normalized.slice(0, 10);
}

function isValidDeluxePhone(value = "") {
  const compact = formatDeluxePhone(value);
  return compact.length === 10 && PHONE_PATTERN.test(compact);
}

function formatEin(value = "") {
  const digits = String(value).replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

function formatSsn(value = "") {
  const digits = String(value).replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function hasOwnerData(owner: any) {
  return Boolean(
    owner.firstName ||
    owner.lastName ||
    owner.ssn ||
    owner.dateOfBirth ||
    owner.residenceAddress?.address ||
    owner.residenceAddress?.zipCode
  );
}

function mergeBusinessProfile(saved: any = {}) {
  return {
    ...defaultBusinessProfile,
    ...saved,
    phoneNumber: saved.phoneNumber ? formatUsPhone(saved.phoneNumber) : "",
    federalTaxId: saved.federalTaxId ? formatEin(saved.federalTaxId) : "",
    businessAddress: {
      ...defaultBusinessProfile.businessAddress,
      ...(saved.businessAddress || {}),
    },
    activeMonths: Array.isArray(saved.activeMonths) && saved.activeMonths.length
      ? saved.activeMonths
      : defaultBusinessProfile.activeMonths,
    numberOfLocation: Number(saved.numberOfLocation || defaultBusinessProfile.numberOfLocation),
  };
}

function mergeOwner(saved: any = {}, index = 0) {
  return {
    ...defaultOwner,
    ...saved,
    type: saved.type || `Owner ${index + 1}`,
    ssn: saved.ssn ? formatSsn(saved.ssn) : "",
    residenceAddress: {
      ...defaultOwner.residenceAddress,
      ...(saved.residenceAddress || {}),
    },
    ownershipPercentage: Number(saved.ownershipPercentage ?? defaultOwner.ownershipPercentage),
  };
}

function mergeTransactionInfo(saved: any = {}) {
  return {
    ...defaultTransactionInfo,
    largestTransactionAmount: Number(saved.largestTransactionAmount || 0),
    monthlySalesVolume: Number(saved.monthlySalesVolume || 0),
  };
}

function getErrorMessage(err: any) {
  const data = err?.response?.data || err;
  const details = data?.details || err?.details;
  const message = data?.message || err?.message;
  const stringify = (value: any) => {
    if (!value) return "";
    return typeof value === "string" ? value : JSON.stringify(value, null, 2);
  };

  if (details && message && details !== message) return `${stringify(message)}: ${stringify(details)}`;
  return stringify(details) || stringify(message) || "Something went wrong";
}

function ApiState({ loading, error, success }: { loading: boolean; error: string; success: string }) {
  return (
    <>
      {loading && (
        <Alert variant="info" className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" /> Working with Deluxe...
        </Alert>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
    </>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="d-flex align-items-start justify-content-between gap-3 mb-3 ">
      <div>
        <h4 className="mb-1">{title}</h4>
        <p className="text-muted mb-0">{subtitle}</p>
      </div>
    </div>
  );
}

function MerchantReviewSkeleton() {
  return (
    <Card className="merchant-review-card border-0 mb-4" aria-label="Loading rates, fees, and agreements">
      <Card.Body>
        <Placeholder as="p" animation="glow" className="mb-2">
          <Placeholder xs={3} />
        </Placeholder>
        <Placeholder as="p" animation="glow" className="mb-4">
          <Placeholder xs={5} />
        </Placeholder>
        {[1, 2].map((item) => (
          <div className="merchant-agreement-card" key={item}>
            <Placeholder as="p" animation="glow" className="mb-3">
              <Placeholder xs={2} />
            </Placeholder>
            <Placeholder as="p" animation="glow" className="mb-2">
              <Placeholder xs={7} />
            </Placeholder>
            <Placeholder as="p" animation="glow" className="mb-2">
              <Placeholder xs={12} />
            </Placeholder>
            <Placeholder as="p" animation="glow" className="mb-0">
              <Placeholder xs={10} />
            </Placeholder>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
}

export default function MerchantPage({ mode }: Props) {
  const navigate = useNavigate();
  const merchantDraft = useMemo(() => readMerchantDraft(), []);
  const [selectedTemplateId, setSelectedTemplateId] = useState(merchantDraft.selectedTemplateId || "");
  const [applicationId, setApplicationId] = useState(localStorage.getItem("deluxeApplicationId") || "");
  const [merchantAccountId, setMerchantAccountId] = useState(localStorage.getItem("merchantAccountId") || "");
  const [businessProfile, setBusinessProfile] = useState<any>(merchantDraft.businessProfile || defaultBusinessProfile);
  const [transactionInfo, setTransactionInfo] = useState<any>(merchantDraft.transactionInfo || defaultTransactionInfo);
  const [owners, setOwners] = useState<any[]>(merchantDraft.owners || [defaultOwner]);
  const [status, setStatus] = useState<any>(null);
  const [currentMerchantAccount, setCurrentMerchantAccount] = useState<any>(null);
  const [existingApplication, setExistingApplication] = useState<any>(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [ratesFees, setRatesFees] = useState<any[]>([]);
  const [acceptanceLanguage, setAcceptanceLanguage] = useState<any[]>([]);
  const [merchantReviewLoading, setMerchantReviewLoading] = useState(false);
  const [acceptedConsents, setAcceptedConsents] = useState<Record<string, boolean>>({});
  const [selectedDecisionOwnerIndex, setSelectedDecisionOwnerIndex] = useState(0);
  const [decisionComment, setDecisionComment] = useState("Submitting merchant decision.");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankRoutingNumber, setBankRoutingNumber] = useState("");
  const [bankAccountType, setBankAccountType] = useState("Checking");
  const [kycSessionId, setKycSessionId] = useState("");
  const [kycCode, setKycCode] = useState("");
  const [kycOwnerType, setKycOwnerType] = useState("Owner 1");
  const [kycPhoneNumber, setKycPhoneNumber] = useState("");
  const [kycQuestions, setKycQuestions] = useState<any[]>([]);
  const [kycAnswers, setKycAnswers] = useState<Record<number, string>>({});
  const [paymentAmount, setPaymentAmount] = useState("");
  const [homeownerEmail, setHomeownerEmail] = useState("");
  const [homeownerFirstName, setHomeownerFirstName] = useState("");
  const [homeownerLastName, setHomeownerLastName] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [paymentLink, setPaymentLink] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [submitProgress, setSubmitProgress] = useState("");

  const activeTab = useMemo(() => routeTabs.find((tab) => tab.key === mode), [mode]);
  const decisionOwners = useMemo(() => owners.filter(hasOwnerData), [owners]);
  const selectedDecisionOwner = decisionOwners[selectedDecisionOwnerIndex] || decisionOwners[0] || defaultOwner;
  const decisionOwnerEmail = String(selectedDecisionOwner?.email || businessProfile.corporateEmail || "").trim();
  const decisionOwnerSsn = String(selectedDecisionOwner?.ssn || "").replace(/\D/g, "").slice(-4);

  const showError = (err: any) => {
    setError(getErrorMessage(err));
    setSuccess("");
  };

  const run = async (callback: () => Promise<void>, successMessage = "") => {
    setLoading(true);
    setError("");
    setSuccess("");
    setValidationErrors([]);
    setSubmitProgress("");
    try {
      await callback();
      if (successMessage) setSuccess(successMessage);
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
      setSubmitProgress("");
    }
  };

  const clearMerchantFormState = () => {
    setApplicationId("");
    setMerchantAccountId("");
    setCurrentMerchantAccount(null);
    setExistingApplication(null);
    setStatus(null);
    setValidationErrors([]);
    setSelectedTemplateId("");
    setBusinessProfile({ ...defaultBusinessProfile });
    setTransactionInfo({ ...defaultTransactionInfo });
    setOwners([{ ...defaultOwner, residenceAddress: { ...defaultOwner.residenceAddress } }]);
    localStorage.removeItem("deluxeApplicationId");
    localStorage.removeItem("merchantAccountId");
    localStorage.removeItem("deluxeMerchantId");
    localStorage.removeItem(MERCHANT_DRAFT_KEY);
  };

  const resetMerchantEnrollment = () => {
    const confirmed = window.confirm(
      "Delete saved merchant enrollment data and clear the form? This removes local records so you can submit again. It does not remove an application already created in Deluxe."
    );
    if (!confirmed) return;

    run(async () => {
      await CallAPIDeletePromise("/v2/api/deluxe/merchant/account/current");
      clearMerchantFormState();
      await loadTemplates();
    }, "Enrollment cleared. You can fill the form and submit again.");
  };

  const pollEnrollmentJob = async (jobId: string) => {
    const maxAttempts = 90;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      setSubmitProgress(`Submitting to Deluxe… step ${attempt + 1} of ${maxAttempts}`);
      const response: any = await CallAPIGetPromise(
        `/v2/api/deluxe/merchant/applications/jobs/${jobId}`
      );
      const job = response?.data || response;

      if (job?.status === "completed") {
        return job;
      }

      if (job?.status === "failed") {
        throw {
          message: job.errorMessage || "Merchant application submission failed.",
          details: job.errorDetails,
        };
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    throw {
      message: `Submission is still processing. Job ID: ${jobId}. Wait a moment and check Status, or try again.`,
    };
  };

  const loadTemplates = async () => {
    try {
      const response: any = await CallAPIGetPromise("/v2/api/deluxe/merchant/templates");
      const list = response?.data || [];
      const firstTemplateId = list[0]?.templateId || "";
      if (firstTemplateId) {
        setSelectedTemplateId((prev:any) => prev || firstTemplateId);
      }
    } catch (err) {
      console.error("Failed to load Deluxe templates:", err);
    }
  };

  const applySavedApplicationDraft = (draft: any) => {
    if (!draft) return;

    if (draft.selectedTemplateId) {
      setSelectedTemplateId(draft.selectedTemplateId);
    }

    if (draft.businessProfile) {
      setBusinessProfile(mergeBusinessProfile(draft.businessProfile));
    }

    if (draft.transactionInfo) {
      setTransactionInfo(mergeTransactionInfo(draft.transactionInfo));
    }

    if (Array.isArray(draft.owners) && draft.owners.length) {
      setOwners(draft.owners.map((owner: any, index: number) => mergeOwner(owner, index)));
    }
  };

  const loadCurrentMerchantAccount = () =>
    run(async () => {
      const response: any = await CallAPIGetPromise("/v2/api/deluxe/merchant/account/current");
      const account = response?.data || null;
      setCurrentMerchantAccount(account);

      if (account?.applicationId) {
        setApplicationId(account.applicationId);
        localStorage.setItem("deluxeApplicationId", account.applicationId);
      }

      if (account?.merchantAccountId) {
        setMerchantAccountId(account.merchantAccountId);
        localStorage.setItem("merchantAccountId", account.merchantAccountId);
      }

      if (account?.merchantId) {
        localStorage.setItem("deluxeMerchantId", account.merchantId);
      }

      if (account?.applicationDraft) {
        applySavedApplicationDraft(account.applicationDraft);
      }
    });

  const validateApplicationPayload = () => {
    const errors: string[] = [];
    const formattedPhone = formatUsPhone(businessProfile.phoneNumber);
    const ownersForSubmit = owners.filter(hasOwnerData);
    const bp = businessProfile;
    const address = bp.businessAddress || {};

    if (!selectedTemplateId) errors.push("Deluxe enrollment template is unavailable. Please refresh the page.");
    if (!bp.dbaName) errors.push("Business DBA name is required.");
    if (!bp.legalName) errors.push("Business legal name is required.");
    if (!PHONE_PATTERN.test(formattedPhone)) errors.push("Business phone must be a valid 10 digit US phone number.");
    if (!bp.corporateEmail || !EMAIL_PATTERN.test(bp.corporateEmail)) errors.push("Business email must be valid.");
    if (!address.address) errors.push("Business street address is required.");
    if (!address.city) errors.push("Business city is required.");
    if (!address.state) errors.push("Business state is required.");
    if (!address.zipCode || !ZIP_PATTERN.test(address.zipCode)) errors.push("Business ZIP must be valid.");
    if (!OWNERSHIP_TYPES.includes(bp.ownershipType)) errors.push("Ownership type must match a Deluxe-supported value.");
    if (!bp.yearsOwned) errors.push("Years owned is required.");
    if (!bp.websiteUrl || !URL_PATTERN.test(bp.websiteUrl)) errors.push("Business website URL must start with http:// or https://.");
    if (!Number(bp.numberOfLocation)) errors.push("Number of locations must be greater than 0.");
    if (!bp.federalTaxId || !EIN_PATTERN.test(bp.federalTaxId)) errors.push("Federal tax ID must use NN-NNNNNNN format.");
    if (!bp.productServiceSold) errors.push("Product/service sold is required.");
    if (!bp.sicCode) errors.push("SIC code is required.");
    if (!Number(transactionInfo.largestTransactionAmount)) errors.push("Largest transaction amount must be greater than 0.");
    if (!Number(transactionInfo.monthlySalesVolume)) errors.push("Monthly sales volume must be greater than 0.");
    if (ownersForSubmit.length === 0) errors.push("At least one owner is required.");

    ownersForSubmit.forEach((owner, index) => {
      const ownerNumber = index + 1;
      const ownerAddress = owner.residenceAddress || {};

      if (owner.type !== `Owner ${ownerNumber}`) errors.push(`Owner ${ownerNumber} type must be Owner ${ownerNumber}.`);
      if (!owner.title) errors.push(`Owner ${ownerNumber} title is required.`);
      if (!owner.firstName) errors.push(`Owner ${ownerNumber} first name is required.`);
      if (!owner.lastName) errors.push(`Owner ${ownerNumber} last name is required.`);
      if (!DATE_PATTERN.test(owner.dateOfBirth || "")) errors.push(`Owner ${ownerNumber} date of birth must use YYYY-MM-DD.`);
      if (!owner.ssn || !SSN_PATTERN.test(owner.ssn)) errors.push(`Owner ${ownerNumber} SSN must use NNN-NN-NNNN format.`);
      if (!Number(owner.ownershipPercentage)) errors.push(`Owner ${ownerNumber} ownership percentage must be greater than 0.`);
      if (!ownerAddress.address) errors.push(`Owner ${ownerNumber} residence address is required.`);
      if (!ownerAddress.city) errors.push(`Owner ${ownerNumber} city is required.`);
      if (!ownerAddress.state) errors.push(`Owner ${ownerNumber} state is required.`);
      if (!ownerAddress.zipCode || !ZIP_PATTERN.test(ownerAddress.zipCode)) errors.push(`Owner ${ownerNumber} ZIP must be valid.`);
    });

    return errors;
  };

  const buildApplicationPayload = () => {
    const ownersForSubmit = owners.filter(hasOwnerData);

    return {
      templateId: selectedTemplateId,
      businessProfile: {
        ...businessProfile,
        phoneNumber: formatUsPhone(businessProfile.phoneNumber),
        numberOfLocation: Number(businessProfile.numberOfLocation || 1),
      },
      transactionInfo: {
        largestTransactionAmount: Number(transactionInfo.largestTransactionAmount || 0),
        monthlySalesVolume: Number(transactionInfo.monthlySalesVolume || 0),
      },
      owners: ownersForSubmit.map((owner, index) => ({
        ...owner,
        type: `Owner ${index + 1}`,
        title: owner.title || "",
        middleName: owner.middleName || "",
        ownershipPercentage: Number(owner.ownershipPercentage || 0),
        residenceAddress: owner.residenceAddress || {},
      })),
    };
  };

  const createApplication = () =>
    run(async () => {
      const errors = validateApplicationPayload();
      if (errors.length) {
        setValidationErrors(errors);
        throw { message: "Please fix the highlighted merchant application fields." };
      }

      const startResponse: any = await CallAPIPostPromise(
        "/v2/api/deluxe/merchant/applications",
        buildApplicationPayload()
      );

      if (startResponse?.success === false) {
        throw startResponse;
      }

      let applicationIdResult = startResponse.applicationId;
      let merchantAccountIdResult = startResponse.merchantAccountId;

      if (startResponse.jobId) {
        const completedJob = await pollEnrollmentJob(startResponse.jobId);
        applicationIdResult = completedJob.applicationId;
        merchantAccountIdResult = completedJob.merchantAccountId;
      }

      if (!applicationIdResult || !merchantAccountIdResult) {
        throw { message: "Application was submitted but IDs were not returned. Check Status or try again." };
      }

      setApplicationId(applicationIdResult);
      setMerchantAccountId(merchantAccountIdResult);
      localStorage.setItem("deluxeApplicationId", applicationIdResult);
      localStorage.setItem("merchantAccountId", merchantAccountIdResult);
      setValidationErrors([]);
    }, "Merchant application created.");

  const refreshStatus = () =>
    run(async () => {
      const response: any = await CallAPIGetPromise(`/v2/api/deluxe/merchant/applications/${applicationId}/status`);
      setStatus(response);
    });

  const fetchExistingApplication = () =>
    run(async () => {
      const response: any = await CallAPIGetPromise(`/v2/api/deluxe/merchant/applications/${applicationId}`);
      setExistingApplication(response?.data || response);
      setShowApplicationModal(true);
    }, "Application details loaded.");

  const parseApiErrorPayload = async (err: any) => {
    const payload = err?.response?.data ?? err?.data ?? err;
    if (payload instanceof Blob) {
      const text = await payload.text();
      try {
        return JSON.parse(text);
      } catch (_error) {
        return { message: text };
      }
    }
    return payload;
  };

  const downloadApplicationPdf = () =>
    run(async () => {
      try {
        const blob: Blob = await api.get(
          `/v2/api/deluxe/merchant/applications/${applicationId}/pdf`,
          { responseType: "blob" }
        );

        if (blob.type?.includes("json")) {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          throw parsed;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `deluxe-application-${applicationId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const parsed = await parseApiErrorPayload(err);
        throw parsed;
      }
    }, "Application PDF downloaded.");

  const fetchRatesFees = () =>
    run(async () => {
      const response: any = await CallAPIGetPromise(`/v2/api/deluxe/merchant/applications/${applicationId}/acceptance-rates-fees`);
      setRatesFees(response?.data || []);
    });

  const fetchAcceptanceLanguage = () =>
    run(async () => {
      const response: any = await CallAPIGetPromise(`/v2/api/deluxe/merchant/applications/${applicationId}/acceptance-language`);
      const agreements = response?.data || [];
      setAcceptanceLanguage(agreements);
      setAcceptedConsents(
        agreements.reduce((consents: Record<string, boolean>, item: any) => {
          if (item?.propertyName && item.propertyName !== "AcceptButton") {
            consents[item.propertyName] = false;
          }
          return consents;
        }, {})
      );
    });

  const loadMerchantReview = () =>
    run(async () => {
      setMerchantReviewLoading(true);
      try {
        const [ratesResponse, agreementsResponse]: any[] = await Promise.all([
          CallAPIGetPromise(`/v2/api/deluxe/merchant/applications/${applicationId}/acceptance-rates-fees`),
          CallAPIGetPromise(`/v2/api/deluxe/merchant/applications/${applicationId}/acceptance-language`),
        ]);
        const agreements = agreementsResponse?.data || [];
        setRatesFees(ratesResponse?.data || []);
        setAcceptanceLanguage(agreements);
        setAcceptedConsents(
          agreements.reduce((consents: Record<string, boolean>, item: any) => {
            if (item?.propertyName && item.propertyName !== "AcceptButton") {
              consents[item.propertyName] = false;
            }
            return consents;
          }, {})
        );
      } finally {
        setMerchantReviewLoading(false);
      }
    });

  const initiateKyc = () =>
    run(async () => {
      const response: any = await CallAPIPostPromise(
        `/v2/api/deluxe/merchant/applications/${applicationId}/identity-verification/initiate`,
        {
          ownerType: kycOwnerType,
          phoneNumber: formatDeluxePhone(kycPhoneNumber),
        }
      );
      const data = response?.data?.data || response?.data || response || {};
      const questions = Array.isArray(data.questions) ? data.questions : [];
      setKycSessionId(data.sessionId || "");
      setKycQuestions(questions);
      setKycCode("");
      setKycAnswers({});
    }, "Identity verification initiated.");

  const submitKyc = () =>
    run(async () => {
      const isKbaFlow = kycQuestions.some((question) => Number(question.id) >= 101);
      const answers = isKbaFlow
        ? kycQuestions
            .filter((question) => Number(question.id) >= 101)
            .map((question) => ({
              questionId: Number(question.id),
              answer: kycAnswers[Number(question.id)],
            }))
        : [{ questionId: 100, answer: kycCode }];

      if (answers.some((answer) => !String(answer.answer || "").trim())) {
        throw {
          message: isKbaFlow
            ? "Please answer all Deluxe identity verification questions."
            : "Please enter the one time passcode.",
        };
      }

      const response: any = await CallAPIPostPromise(
        `/v2/api/deluxe/merchant/applications/${applicationId}/identity-verification/submit`,
        {
          sessionId: kycSessionId,
          answers,
        }
      );
      const data = response?.data?.data || response?.data || response || {};
      const questions = Array.isArray(data.questions) ? data.questions : [];

      if (data.sessionId) {
        setKycSessionId(data.sessionId);
      }

      if (questions.length > 0) {
        setKycQuestions(questions);
        setKycCode("");
        setKycAnswers({});
        throw {
          message: "OTP verification returned KBA questions. Please answer the Deluxe verification questions.",
        };
      }
    }, "Identity verification submitted.");

  const submitMerchantDecision = () =>
    run(async () => {
      if (!ratesFees.length || !acceptanceLanguage.length) {
        throw { message: "Rates, fees, and acceptance terms must load before submitting." };
      }

      const requiredConsents = acceptanceLanguage.filter(
        (item: any) => item?.isConsentRequired && item?.propertyName !== "AcceptButton"
      );
      const missingConsent = requiredConsents.find((item: any) => !acceptedConsents[item.propertyName]);
      if (missingConsent) {
        throw { message: "Please accept every required agreement before submitting." };
      }

      if (!selectedDecisionOwner?.firstName || !selectedDecisionOwner?.lastName || !decisionOwnerEmail || !decisionOwnerSsn) {
        throw { message: "The selected owner's name, email, and SSN must be available in the merchant application." };
      }

      if (!isValidAbaRoutingNumber(bankRoutingNumber)) {
        throw { message: "Enter a valid 9-digit ABA routing number." };
      }

      if (!isValidBankAccountNumber(bankAccountNumber)) {
        throw { message: "Enter a valid bank account number containing 8 to 17 digits." };
      }

      const dynamicDecisionFlags = acceptanceLanguage.reduce((consents: any, item: any) => {
        if (item?.propertyName && item.propertyName !== "AcceptButton") {
          const normalizedName = item.propertyName.charAt(0).toLowerCase() + item.propertyName.slice(1);
          consents[normalizedName] = Boolean(acceptedConsents[item.propertyName]);
        }
        return consents;
      }, {});
      const acceptedAt = new Date().toISOString();

      await CallAPIPostPromise(
        `/v2/api/deluxe/merchant/applications/${applicationId}/submit`,
        {
          decision: {
            acceptorEmail: decisionOwnerEmail,
            ...dynamicDecisionFlags,
          },
          ownerInfo: {
            isApprovedByOwner: true,
            firstName: selectedDecisionOwner.firstName,
            lastName: selectedDecisionOwner.lastName,
            email: decisionOwnerEmail,
            ssn: decisionOwnerSsn,
          },
          deviceContext: {
            browserInfo: window.navigator.userAgent,
            deviceInfo: /Mobi|Android/i.test(window.navigator.userAgent) ? "mobile" : "desktop",
          },
          comment: decisionComment,
          acceptanceEvidence: {
            acceptedAt,
            ratesFees,
            acceptanceLanguage,
            acceptedConsents,
          },
          bankDetails: [
            {
              directDepositAccounts: [
                {
                  accountNumber: bankAccountNumber,
                  accountType: bankAccountType,
                  ddaActivity: "All",
                  routingNumber: bankRoutingNumber,
                },
              ],
            },
          ],
        }
      );
    }, "Merchant decision submitted.");

  const createPaymentLink = () =>
    run(async () => {
      const response: any = await CallAPIPostPromise(
        "/v2/api/deluxe/payment-links",
        {
          merchantAccountId,
          invoiceId,
          amount: Number(paymentAmount),
          currency: "USD",
          homeowner: {
            firstName: homeownerFirstName,
            lastName: homeownerLastName,
            email: homeownerEmail,
          },
          description: invoiceId ? `Invoice ${invoiceId}` : "Tradesmens payment",
          expiry: "9 DAYS",
        }
      );
      setPaymentLink(response?.data);
    }, "Payment link created.");

  useEffect(() => {
    loadTemplates();
    loadCurrentMerchantAccount();
    if ((mode === "status" || mode === "payment-setup") && applicationId) refreshStatus();
    if (mode === "payment-setup" && applicationId) loadMerchantReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    localStorage.setItem(
      MERCHANT_DRAFT_KEY,
      JSON.stringify({
        selectedTemplateId,
        businessProfile,
        transactionInfo,
        owners,
      })
    );
  }, [selectedTemplateId, businessProfile, transactionInfo, owners]);

  const updateBusiness = (path: string, value: any) => {
    if (path.includes(".")) {
      const [parent, child] = path.split(".");
      setBusinessProfile((prev: any) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
      return;
    }
    setBusinessProfile((prev: any) => ({ ...prev, [path]: value }));
  };

  const updateOwner = (index: number, path: string, value: any) => {
    setOwners((prev) =>
      prev.map((owner, ownerIndex) => {
        if (ownerIndex !== index) return owner;
        if (path.includes(".")) {
          const [parent, child] = path.split(".");
          return {
            ...owner,
            [parent]: {
              ...owner[parent],
              [child]: value,
            },
          };
        }
        return { ...owner, [path]: value };
      })
    );
  };

  const currentValidationErrors = useMemo(
    () => validationErrors.length ? validateApplicationPayload() : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationErrors.length, selectedTemplateId, businessProfile, transactionInfo, owners]
  );
  const hasValidationError = (message: string) => currentValidationErrors.includes(message);
  const hasValidationMatch = (matcher: (message: string) => boolean) => currentValidationErrors.some(matcher);

  const applyZipLookup = async (zip: string, target: "business" | "owner", ownerIndex?: number) => {
    const cleanZip = String(zip || "").trim();
    if (!/^\d{5}$/.test(cleanZip)) return;

    try {
      const response = await fetch(`${apiUrl}/misc/zipcode/US/${cleanZip}`);
      if (!response.ok) return;

      const data = await response.json();
      const place = data?.places?.[0];
      if (!place) return;

      const city = place["place name"] || "";
      const state = place["state abbreviation"] || "";

      if (target === "business") {
        setBusinessProfile((prev: any) => ({
          ...prev,
          businessAddress: {
            ...prev.businessAddress,
            city: prev.businessAddress.city || city,
            state: prev.businessAddress.state || state,
          },
        }));
        return;
      }

      if (typeof ownerIndex === "number") {
        setOwners((prev) =>
          prev.map((owner, index) =>
            index === ownerIndex
              ? {
                  ...owner,
                  residenceAddress: {
                    ...owner.residenceAddress,
                    city: owner.residenceAddress.city || city,
                    state: owner.residenceAddress.state || state,
                  },
                }
              : owner
          )
        );
      }
    } catch (_error) {
      // ZIP autofill is a convenience only; never block onboarding on it.
    }
  };

  const statusData = status?.data || status || {};
  const deluxeApplicationStatus = statusData?.applicationStatus || "";
  const deluxeMerchantStatus = statusData?.merchantStatus || "";
  const deluxeMerchantId = statusData?.merchantId || merchantAccountId || "";
  const tradesmensMerchantStatus = status?.tradesmensStatus || getTradesmensStatus(statusData);
  const effectiveStepKey = deluxeMerchantStatus === "ACTIVE" ? "ACTIVE" : deluxeApplicationStatus;
  const currentStepIndex = APPLICATION_STATUS_STEPS.findIndex((step) => step.key === effectiveStepKey);
  const isRejected = deluxeApplicationStatus === "REJECTED";
  const documentedStatusResponse = {
    merchantId: deluxeMerchantId || null,
    applicationStatus: deluxeApplicationStatus || null,
    merchantStatus: deluxeMerchantStatus || null,
  };
  const requiredConsentItems = acceptanceLanguage.filter(
    (item: any) => item?.isConsentRequired && item?.propertyName !== "AcceptButton"
  );
  const acceptedConsentCount = requiredConsentItems.filter(
    (item: any) => acceptedConsents[item.propertyName]
  ).length;
  const consentProgress = requiredConsentItems.length
    ? Math.round((acceptedConsentCount / requiredConsentItems.length) * 100)
    : 0;

  return (
    <div className="page-content">
      <Container fluid>
        <SectionHeader
          title="Deluxe Merchant Enrollment"
          subtitle="Contractor onboarding, KYC submission, merchant approval, and hosted payment link creation."
        />

        <Nav variant="tabs" activeKey={activeTab?.key} className="mb-4">
          {routeTabs.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link eventKey={tab.key} onClick={() => navigate(tab.path)}>
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <ApiState loading={loading} error={error} success={success} />

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          {currentMerchantAccount ? (
            <Alert variant="light" className="border d-flex flex-wrap align-items-center gap-3 mb-0 flex-grow-1">
              <div>
                <div className="fw-semibold">Stored merchant account</div>
                <div className="text-muted small">
                  Application {currentMerchantAccount.applicationId || "pending"} · Tradesmens{" "}
                  {currentMerchantAccount.tradesmensStatus || "draft"} · Deluxe merchant{" "}
                  {currentMerchantAccount.merchantId || "pending"}
                </div>
              </div>
            </Alert>
          ) : (
            <div className="text-muted small flex-grow-1">No saved enrollment yet. Complete Business and Owners, then submit.</div>
          )}
          <Button variant="outline-danger" onClick={resetMerchantEnrollment} disabled={loading}>
            <Trash2 size={16} className="me-2" /> Reset enrollment
          </Button>
        </div>

        {submitProgress ? (
          <Alert variant="info" className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" /> {submitProgress}
          </Alert>
        ) : null}

        {mode === "business-info" && (
          <Card>
            <Card.Body>
              <SectionHeader title="Business Info" subtitle="Collect the business profile and transaction volume Deluxe requires." />
              {currentValidationErrors.some((validationError) => validationError.startsWith("Business") || validationError.includes("transaction") || validationError.includes("Ownership") || validationError.includes("Federal") || validationError.includes("Product") || validationError.includes("SIC") || validationError.includes("Number of locations")) && (
                <Alert variant="danger">
                  <div className="fw-semibold mb-2">Business information needs attention:</div>
                  <ul className="mb-0 ps-3">
                    {currentValidationErrors
                      .filter((validationError) => validationError.startsWith("Business") || validationError.includes("transaction") || validationError.includes("Ownership") || validationError.includes("Federal") || validationError.includes("Product") || validationError.includes("SIC") || validationError.includes("Number of locations"))
                      .map((validationError) => (
                        <li key={validationError}>{validationError}</li>
                      ))}
                  </ul>
                </Alert>
              )}
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>DBA Name</Form.Label>
                  <Form.Control placeholder="Example: ABC Store" value={businessProfile.dbaName} onChange={(e) => updateBusiness("dbaName", e.target.value)} isInvalid={hasValidationError("Business DBA name is required.")} />
                </Col>
                <Col md={6}>
                  <Form.Label>Legal Business Name</Form.Label>
                  <Form.Control placeholder="Example: ABC Store LLC" value={businessProfile.legalName} onChange={(e) => updateBusiness("legalName", e.target.value)} isInvalid={hasValidationError("Business legal name is required.")} />
                </Col>
                <Col md={4}>
                  <Form.Label>Business Phone</Form.Label>
                  <Form.Control
                    placeholder="Example: 123-456-7890"
                    value={businessProfile.phoneNumber}
                    onChange={(e) => updateBusiness("phoneNumber", e.target.value)}
                    onBlur={(e) => updateBusiness("phoneNumber", formatUsPhone(e.target.value))}
                    isInvalid={hasValidationError("Business phone must be a valid 10 digit US phone number.")}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Corporate Email</Form.Label>
                  <Form.Control placeholder="Example: info@abcstore.com" value={businessProfile.corporateEmail} onChange={(e) => updateBusiness("corporateEmail", e.target.value)} isInvalid={hasValidationError("Business email must be valid.")} />
                </Col>
                <Col md={4}>
                  <Form.Label>Federal Tax ID</Form.Label>
                  <Form.Control
                    placeholder="Example: 12-3456789"
                    value={businessProfile.federalTaxId}
                    onChange={(e) => updateBusiness("federalTaxId", formatEin(e.target.value))}
                    isInvalid={hasValidationError("Federal tax ID must use NN-NNNNNNN format.")}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Business Street Address</Form.Label>
                  <Form.Control placeholder="Example: 123 Main Street Suite 101" value={businessProfile.businessAddress.address} onChange={(e) => updateBusiness("businessAddress.address", e.target.value)} isInvalid={hasValidationError("Business street address is required.")} />
                </Col>
                <Col md={2}>
                  <Form.Label>City</Form.Label>
                  <Form.Control placeholder="Example: Dallas" value={businessProfile.businessAddress.city} onChange={(e) => updateBusiness("businessAddress.city", e.target.value)} isInvalid={hasValidationError("Business city is required.")} />
                </Col>
                <Col md={2}>
                  <Form.Label>State</Form.Label>
                  <Form.Control placeholder="Example: TX" value={businessProfile.businessAddress.state} onChange={(e) => updateBusiness("businessAddress.state", e.target.value)} isInvalid={hasValidationError("Business state is required.")} />
                </Col>
                <Col md={2}>
                  <Form.Label>ZIP Code</Form.Label>
                  <Form.Control
                    placeholder="Example: 75201"
                    value={businessProfile.businessAddress.zipCode}
                    onChange={(e) => updateBusiness("businessAddress.zipCode", e.target.value)}
                    onBlur={(e) => applyZipLookup(e.target.value, "business")}
                    isInvalid={hasValidationError("Business ZIP must be valid.")}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Ownership Type</Form.Label>
                  <Form.Select
                    value={businessProfile.ownershipType}
                    onChange={(e) => updateBusiness("ownershipType", e.target.value)}
                    isInvalid={hasValidationError("Ownership type must match a Deluxe-supported value.")}
                  >
                    {OWNERSHIP_TYPES.map((ownershipType) => (
                      <option key={ownershipType} value={ownershipType}>{ownershipType}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label>Years Owned</Form.Label>
                  <Form.Control placeholder="Example: 10" value={businessProfile.yearsOwned} onChange={(e) => updateBusiness("yearsOwned", e.target.value)} isInvalid={hasValidationError("Years owned is required.")} />
                </Col>
                <Col md={12}>
                  <SicCodeSearchField
                    value={businessProfile.sicCode}
                    onChange={(sicCode) => updateBusiness("sicCode", sicCode)}
                    isInvalid={hasValidationError("SIC code is required.")}
                    productHint={businessProfile.productServiceSold}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Number of Locations</Form.Label>
                  <Form.Control type="number" placeholder="Example: 3" value={businessProfile.numberOfLocation} onChange={(e) => updateBusiness("numberOfLocation", Number(e.target.value))} isInvalid={hasValidationError("Number of locations must be greater than 0.")} />
                </Col>
                <Col md={8}>
                  <Form.Label>Website URL</Form.Label>
                  <Form.Control placeholder="Example: https://www.abcstore.com" value={businessProfile.websiteUrl} onChange={(e) => updateBusiness("websiteUrl", e.target.value)} isInvalid={hasValidationError("Business website URL must start with http:// or https://.")} />
                </Col>
                <Col md={6}>
                  <Form.Label>Products or Services Sold</Form.Label>
                  <Form.Control placeholder="Example: Electronics and Home Appliances" value={businessProfile.productServiceSold} onChange={(e) => updateBusiness("productServiceSold", e.target.value)} isInvalid={hasValidationError("Product/service sold is required.")} />
                </Col>
                <Col md={6}>
                  <Form.Label>Largest Transaction Amount</Form.Label>
                  <Form.Control type="number" placeholder="Example: 5000" value={transactionInfo.largestTransactionAmount} onChange={(e) => setTransactionInfo({ ...transactionInfo, largestTransactionAmount: Number(e.target.value) })} isInvalid={hasValidationError("Largest transaction amount must be greater than 0.")} />
                </Col>
                <Col md={6}>
                  <Form.Label>Monthly Sales Volume</Form.Label>
                  <Form.Control type="number" placeholder="Example: 20000" value={transactionInfo.monthlySalesVolume} onChange={(e) => setTransactionInfo({ ...transactionInfo, monthlySalesVolume: Number(e.target.value) })} isInvalid={hasValidationError("Monthly sales volume must be greater than 0.")} />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {mode === "owners" && (
          <Card>
            <Card.Body>
              <SectionHeader title="Owners" subtitle="Add beneficial owner details, then submit the Deluxe application." />
              {currentValidationErrors.length > 0 && (
                <Alert variant="danger">
                  <div className="fw-semibold mb-2">Please fix these fields before submitting:</div>
                  <ul className="mb-0 ps-3">
                    {currentValidationErrors.map((validationError) => (
                      <li key={validationError}>{validationError}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              {owners.map((owner, index) => (
                <Card className="mb-3" key={index}>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={2}>
                        <Form.Label>Owner Type</Form.Label>
                        <Form.Control value={`Owner ${index + 1}`} disabled />
                      </Col>
                      <Col md={2}>
                        <Form.Label>Title</Form.Label>
                        <Form.Control placeholder="Example: CEO" value={owner.title} onChange={(e) => updateOwner(index, "title", e.target.value)} isInvalid={hasValidationError(`Owner ${index + 1} title is required.`)} />
                      </Col>
                      <Col md={3}>
                        <Form.Label>First Name</Form.Label>
                        <Form.Control placeholder="Example: John" value={owner.firstName} onChange={(e) => updateOwner(index, "firstName", e.target.value)} isInvalid={hasValidationError(`Owner ${index + 1} first name is required.`)} />
                      </Col>
                      <Col md={2}>
                        <Form.Label>Middle Name</Form.Label>
                        <Form.Control placeholder="Example: M" value={owner.middleName} onChange={(e) => updateOwner(index, "middleName", e.target.value)} />
                      </Col>
                      <Col md={3}>
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control placeholder="Example: Doe" value={owner.lastName} onChange={(e) => updateOwner(index, "lastName", e.target.value)} isInvalid={hasValidationError(`Owner ${index + 1} last name is required.`)} />
                      </Col>
                      <Col md={3}>
                        <Form.Label>Ownership Percentage</Form.Label>
                        <Form.Control type="number" placeholder="Example: 60" value={owner.ownershipPercentage} onChange={(e) => updateOwner(index, "ownershipPercentage", Number(e.target.value))} isInvalid={hasValidationError(`Owner ${index + 1} ownership percentage must be greater than 0.`)} />
                      </Col>
                      <Col md={3}>
                        <Form.Label>Date of Birth</Form.Label>
                        <Form.Control type="date" placeholder="Example: 1985-03-15" value={owner.dateOfBirth} onChange={(e) => updateOwner(index, "dateOfBirth", e.target.value)} isInvalid={hasValidationError(`Owner ${index + 1} date of birth must use YYYY-MM-DD.`)} />
                      </Col>
                      <Col md={4}>
                        <Form.Label>SSN</Form.Label>
                        <Form.Control placeholder="Example: 123-45-6789" value={owner.ssn} onChange={(e) => updateOwner(index, "ssn", formatSsn(e.target.value))} isInvalid={hasValidationError(`Owner ${index + 1} SSN must use NNN-NN-NNNN format.`)} />
                      </Col>
                      <Col md={5}>
                        <Form.Label>Residence Address</Form.Label>
                        <Form.Control placeholder="Example: 123 Main Street Suite 101" value={owner.residenceAddress.address} onChange={(e) => updateOwner(index, "residenceAddress.address", e.target.value)} isInvalid={hasValidationError(`Owner ${index + 1} residence address is required.`)} />
                      </Col>
                      <Col md={4}>
                        <Form.Label>City</Form.Label>
                        <Form.Control placeholder="Example: Dallas" value={owner.residenceAddress.city} onChange={(e) => updateOwner(index, "residenceAddress.city", e.target.value)} isInvalid={hasValidationError(`Owner ${index + 1} city is required.`)} />
                      </Col>
                      <Col md={4}>
                        <Form.Label>State</Form.Label>
                        <Form.Control placeholder="Example: TX" value={owner.residenceAddress.state} onChange={(e) => updateOwner(index, "residenceAddress.state", e.target.value)} isInvalid={hasValidationError(`Owner ${index + 1} state is required.`)} />
                      </Col>
                      <Col md={4}>
                        <Form.Label>ZIP Code</Form.Label>
                        <Form.Control
                          placeholder="Example: 75201"
                          value={owner.residenceAddress.zipCode}
                          onChange={(e) => updateOwner(index, "residenceAddress.zipCode", e.target.value)}
                          onBlur={(e) => applyZipLookup(e.target.value, "owner", index)}
                          isInvalid={hasValidationError(`Owner ${index + 1} ZIP must be valid.`)}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    setOwners([
                      ...owners,
                      {
                        ...defaultOwner,
                        type: `Owner ${owners.length + 1}`,
                        ownershipPercentage: 0,
                        residenceAddress: { ...defaultOwner.residenceAddress },
                      },
                    ])
                  }
                >
                  Add Owner
                </Button>
                <Button onClick={createApplication} disabled={loading || Boolean(applicationId)}>
                  <Send size={16} className="me-2" /> Submit Application
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {mode === "kyc" && (
          <Card>
            <Card.Body>
              <SectionHeader title="KYC Submission" subtitle="Submit the Deluxe identity verification session using OTP or KBA challenge answers." />
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Application ID</Form.Label>
                  <Form.Control placeholder="Example: 725113" value={applicationId} onChange={(e) => setApplicationId(e.target.value)} />
                </Col>
                <Col md={3}>
                  <Form.Label>Owner Type</Form.Label>
                  <Form.Select value={kycOwnerType} onChange={(e) => setKycOwnerType(e.target.value)}>
                    {Array.from({ length: 10 }).map((_, index) => (
                      <option key={index + 1} value={`Owner ${index + 1}`}>{`Owner ${index + 1}`}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label>OTP Phone Number</Form.Label>
                  <Form.Control
                    placeholder="Example: 8007031111"
                    inputMode="numeric"
                    maxLength={12}
                    value={kycPhoneNumber}
                    onChange={(e) => setKycPhoneNumber(e.target.value.replace(/[^\d+\-()\s]/g, ""))}
                    onBlur={(e) => setKycPhoneNumber(formatDeluxePhone(e.target.value))}
                    isInvalid={Boolean(kycPhoneNumber) && !isValidDeluxePhone(kycPhoneNumber)}
                  />
                  <Form.Text className="text-muted">10-digit US number, digits only (Deluxe format).</Form.Text>
                </Col>
                <Col md={7}>
                  <Form.Label>Identity Verification Session ID</Form.Label>
                  <Form.Control placeholder="Example: SGEXRMFBXVOTTOELHSFJJJGJ.pidd4v..." value={kycSessionId} onChange={(e) => setKycSessionId(e.target.value)} />
                </Col>
                {kycQuestions.some((question) => Number(question.id) === 100) && (
                  <Col md={3}>
                    <Form.Label>One Time Passcode</Form.Label>
                    <Form.Control placeholder="Example: 123456" value={kycCode} onChange={(e) => setKycCode(e.target.value)} />
                  </Col>
                )}
              </Row>
              <div className="d-flex flex-wrap gap-2 mt-3">
                <Button onClick={initiateKyc} disabled={loading || !applicationId || !kycOwnerType || !isValidDeluxePhone(kycPhoneNumber)}>
                  <ShieldCheck size={16} className="me-2" /> Initiate Verification
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={submitKyc}
                  disabled={
                    loading ||
                    !applicationId ||
                    !kycSessionId ||
                    (kycQuestions.some((question) => Number(question.id) >= 101)
                      ? kycQuestions
                          .filter((question) => Number(question.id) >= 101)
                          .some((question) => !kycAnswers[Number(question.id)])
                      : !kycCode)
                  }
                >
                  <ShieldCheck size={16} className="me-2" /> Submit Verification
                </Button>
              </div>
              {kycQuestions.length > 0 && (
                <Alert variant="light" className="border mt-3 mb-0">
                  <div className="fw-semibold mb-2">Deluxe verification challenge</div>
                  {kycQuestions.map((question) => {
                    const questionId = Number(question.id);
                    const choices = Array.isArray(question.choices) ? question.choices : [];

                    return (
                      <div key={question.id} className="mb-3">
                        <div className="text-muted mb-2">{question.id}: {question.text}</div>
                        {questionId >= 101 && choices.length > 0 && (
                          <div className="d-grid gap-2">
                            {choices.map((choice: string, choiceIndex: number) => {
                              const answerValue = String(choiceIndex + 1);
                              return (
                              <Form.Check
                                key={`${question.id}-${choice}`}
                                type="radio"
                                id={`kyc-${question.id}-${choice}`}
                                name={`kyc-question-${question.id}`}
                                label={choice}
                                value={answerValue}
                                checked={kycAnswers[questionId] === answerValue}
                                onChange={() =>
                                  setKycAnswers((prev) => ({
                                    ...prev,
                                    [questionId]: answerValue,
                                  }))
                                }
                              />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Alert>
              )}
            </Card.Body>
          </Card>
        )}

        {mode === "status" && (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="bg-primary bg-opacity-10 text-primary rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38 }}>
                      <FileSearch size={20} />
                    </span>
                    <div>
                      <h4 className="mb-0">Merchant Status</h4>
                      <p className="text-muted mb-0">Track Deluxe approval and Tradesmens payment readiness.</p>
                    </div>
                  </div>
                </div>
                <Button variant="outline-primary" onClick={refreshStatus} disabled={loading || !applicationId}>
                  <RefreshCw size={16} className="me-2" /> Refresh
                </Button>
              </div>

              <Row className="g-3 align-items-end mb-4">
                <Col lg={7}>
                  <Form.Label className="fw-semibold">Deluxe Application ID</Form.Label>
                  <Form.Control
                    placeholder="Enter Deluxe application ID"
                    value={applicationId}
                    onChange={(e) => setApplicationId(e.target.value)}
                  />
                </Col>
                <Col lg={5}>
                  <div className="d-flex flex-wrap gap-2">
                    <Button onClick={refreshStatus} disabled={loading || !applicationId}>
                      <RefreshCw size={16} className="me-2" /> Check Status
                    </Button>
                    <Button variant="outline-primary" onClick={fetchExistingApplication} disabled={loading || !applicationId}>
                      <FileSearch size={16} className="me-2" /> Get Application
                    </Button>
                    <Button variant="outline-dark" onClick={downloadApplicationPdf} disabled={loading || !applicationId}>
                      <FileText size={16} className="me-2" /> Download PDF
                    </Button>
                    <Button variant="outline-secondary" onClick={() => navigate("/merchant/payment-setup")} disabled={!applicationId}>
                      <CreditCard size={16} className="me-2" /> Payment Setup
                    </Button>
                  </div>
                </Col>
              </Row>

              {!status && (
                <Alert variant="light" className="border d-flex align-items-start gap-3 mb-0">
                  <Clock3 size={20} className="text-primary mt-1" />
                  <div>
                    <div className="fw-semibold">No status loaded yet</div>
                    <div className="text-muted">Enter an application ID or submit an application from the Owners page, then refresh this screen.</div>
                  </div>
                </Alert>
              )}

              {status && (
                <div>
                  <Row className="g-3 mb-4">
                    <Col xl={3} md={6}>
                      <div className="border rounded-3 p-3 h-100">
                        <div className="text-muted small mb-2">applicationStatus</div>
                        <Badge bg={getBadgeVariant(deluxeApplicationStatus)}>{deluxeApplicationStatus || "UNKNOWN"}</Badge>
                      </div>
                    </Col>
                    <Col xl={3} md={6}>
                      <div className="border rounded-3 p-3 h-100">
                        <div className="text-muted small mb-2">merchantStatus</div>
                        <Badge bg={getBadgeVariant(deluxeMerchantStatus)}>{deluxeMerchantStatus || "PENDING"}</Badge>
                      </div>
                    </Col>
                    <Col xl={3} md={6}>
                      <div className="border rounded-3 p-3 h-100">
                        <div className="text-muted small mb-2">merchantId</div>
                        <div className="fw-semibold text-break">{deluxeMerchantId || "Pending"}</div>
                      </div>
                    </Col>
                    <Col xl={3} md={6}>
                      <div className="border rounded-3 p-3 h-100">
                        <div className="text-muted small mb-2">Tradesmens Readiness</div>
                        <Badge bg={getBadgeVariant(tradesmensMerchantStatus)} className="text-uppercase">{tradesmensMerchantStatus}</Badge>
                      </div>
                    </Col>
                  </Row>

                  {isRejected && (
                    <Alert variant="danger" className="d-flex align-items-start gap-2">
                      <XCircle size={18} className="mt-1" />
                      <div>
                        <div className="fw-semibold">Application rejected by Deluxe</div>
                        <div>Review the response with the contractor and Deluxe support before resubmitting.</div>
                      </div>
                    </Alert>
                  )}

                  <div className="border rounded-3 p-3 mb-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <Building2 size={18} className="text-primary" />
                      <h5 className="mb-0">Approval Progress</h5>
                    </div>
                    <Row className="g-3">
                      {APPLICATION_STATUS_STEPS.map((step, index) => {
                        const isDone = currentStepIndex > index;
                        const isCurrent = currentStepIndex === index;
                        const stepVariant = isDone ? "success" : isCurrent ? getBadgeVariant(step.key) : "secondary";

                        return (
                          <Col xxl={2} lg={4} md={6} key={step.key}>
                            <div className={`border rounded-3 p-3 h-100 ${isCurrent ? "border-primary bg-primary bg-opacity-10" : ""}`}>
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <Badge bg={stepVariant}>{index + 1}</Badge>
                                {isDone && <CheckCircle2 size={18} className="text-success" />}
                                {isCurrent && !isDone && <Clock3 size={18} className="text-primary" />}
                              </div>
                              <div className="fw-semibold">{step.label}</div>
                              <div className="text-muted small">{step.description}</div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>

                  <Row className="g-3">
                    <Col lg={7}>
                      <div className="border rounded-3 p-3 h-100">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <AlertCircle size={18} className="text-secondary" />
                          <h5 className="mb-0">Documented Status Response</h5>
                        </div>
                        <pre className="bg-light p-3 rounded mb-0" style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(documentedStatusResponse, null, 2)}</pre>
                      </div>
                    </Col>
                    <Col lg={5}>
                      <div className="border rounded-3 p-3 h-100">
                        <h5 className="mb-3">Next Actions</h5>
                        <div className="d-grid gap-2">
                          <Button variant="outline-primary" onClick={() => navigate("/merchant/payment-setup")} disabled={!applicationId}>
                            Review Rates & Fees
                          </Button>
                          <Button variant="outline-secondary" onClick={() => navigate("/merchant/kyc")}>
                            Continue KYC
                          </Button>
                          <Button variant="outline-success" onClick={() => navigate("/merchant/payments")} disabled={deluxeMerchantStatus !== "ACTIVE"}>
                            Create Payment Link
                          </Button>
                        </div>
                        {deluxeMerchantStatus !== "ACTIVE" && (
                          <p className="text-muted small mt-3 mb-0">
                            Payment links stay disabled until Deluxe returns merchantStatus ACTIVE.
                          </p>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {existingApplication && (
                    <div className="border rounded-3 p-3 mt-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <div>
                        <div className="fw-semibold">Application details loaded</div>
                        <div className="text-muted small">Open the modal to review business, transaction, and owner data.</div>
                      </div>
                      <Button variant="outline-primary" onClick={() => setShowApplicationModal(true)}>
                        View Application
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <ApplicationDetailModal
          show={showApplicationModal}
          onHide={() => setShowApplicationModal(false)}
          application={existingApplication}
        />

        {mode === "payment-setup" && (
          <div className="merchant-review-shell">
            <div className="merchant-review-header mb-4">
              <div>
                <div className="merchant-review-eyebrow">Deluxe merchant enrollment</div>
                <h4 className="mb-1">Review your offer and accept terms</h4>
                <p className="text-muted mb-0">Your pricing and agreements are shown below exactly as they will be submitted to Deluxe.</p>
              </div>
              <div className="merchant-review-step">Step 2 of 3 · Agreements</div>
            </div>
            <Card className="merchant-review-card border-0 mb-4">
            <Card.Body>
              <SectionHeader title="Rates & Fees" subtitle="Review the payment-processing pricing for this application." />
              <Row className="g-3 align-items-end mb-3">
                <Col md={6}>
                  <Form.Label>Application ID</Form.Label>
                  <Form.Control placeholder="Example: 725113" value={applicationId} onChange={(e) => setApplicationId(e.target.value)} />
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap gap-2">
                   
                    <Button variant="outline-dark" onClick={downloadApplicationPdf} disabled={loading || !applicationId}>Download PDF</Button>
                  </div>
                </Col>
              </Row>
              {!merchantReviewLoading && applicationId && !ratesFees.length && (
                <Alert variant="warning">Rates and fees could not be loaded. Refresh disclosures before submitting.</Alert>
              )}
              {merchantReviewLoading ? (
                <Row className="g-3">
                  {[1, 2].map((item) => (
                    <Col lg={6} key={item}>
                      <Card className="merchant-rate-card h-100">
                        <Card.Body>
                          <Placeholder as="p" animation="glow" className="mb-3"><Placeholder xs={5} /></Placeholder>
                          {[1, 2, 3, 4].map((line) => (
                            <Placeholder as="p" animation="glow" className="mb-2" key={line}><Placeholder xs={12} /></Placeholder>
                          ))}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : <Row className="g-3">
              {ratesFees.map((section) => (
                <Col lg={6} key={section.sectionName}>
                <Card className="merchant-rate-card h-100">
                  <Card.Body>
                    <div className="merchant-rate-card-label">Pricing detail</div>
                    <h5>{section.sectionDescription || section.sectionName}</h5>
                    <Table responsive size="sm" className="mb-0">
                      <tbody>
                        {(section.fields || []).map((field: any) => (
                          <tr key={`${section.sectionName}-${field.label}`}>
                            <td>{field.label}</td>
                            <td className="text-muted">{field.description}</td>
                            <td className="text-end fw-semibold">{field.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
                </Col>
              ))}
              </Row>}
            </Card.Body>
            </Card>
              {merchantReviewLoading ? <MerchantReviewSkeleton /> : acceptanceLanguage.length > 0 && (
                <Card className="merchant-review-card border-0 mb-4">
                  <Card.Body>
                    <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
                      <div>
                        <div className="merchant-review-eyebrow">Step 2 of 3</div>
                        <h4 className="mb-1">Terms & Agreements</h4>
                        <p className="text-muted mb-0">Read each agreement and confirm the required acknowledgements.</p>
                      </div>
                      <div className="text-muted small fw-semibold">
                        {acceptedConsentCount} of {requiredConsentItems.length} required agreements accepted
                      </div>
                    </div>
                    {acceptanceLanguage.map((item) => (
                      <div className={`merchant-agreement-card ${acceptedConsents[item.propertyName] ? "is-accepted" : ""}`} key={item.propertyName}>
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                          <div>
                            <div className="merchant-rate-card-label">Deluxe agreement</div>
                            <h5 className="mb-0">{formatConsentTitle(item.propertyName)}</h5>
                          </div>
                          <Badge bg={item.isConsentRequired ? "warning" : "secondary"}>
                            {item.isConsentRequired ? "Consent required" : "Optional"}
                          </Badge>
                        </div>
                        <div className="merchant-agreement-copy" dangerouslySetInnerHTML={{ __html: sanitizeAgreementHtml(item.agreement) }} />
                        {item.propertyName !== "AcceptButton" && item.isConsentRequired && (
                          <Form.Check
                            className="merchant-consent-check mt-4"
                            id={`merchant-consent-${item.propertyName}`}
                            label="I have read and agree to this required agreement."
                            checked={Boolean(acceptedConsents[item.propertyName])}
                            onChange={(event) =>
                              setAcceptedConsents((consents) => ({
                                ...consents,
                                [item.propertyName]: event.target.checked,
                              }))
                            }
                          />
                        )}
                        {item.propertyName === "AcceptButton" && (
                          <div className="merchant-electronic-signature mt-3">Your electronic acknowledgement is applied when you select Accept & Submit below.</div>
                        )}
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              )}
              <Card className="merchant-review-card border-0">
                <Card.Body>
                  <div className="merchant-review-eyebrow">Step 3 of 3</div>
                  <h4 className="mb-3">Confirm account details</h4>
                  <Alert variant="info" className="border-0">Clicking Accept & Submit is your electronic acknowledgement of the displayed terms.</Alert>
                  <div className="mb-3">
                    <div className="fw-semibold">Owner information</div>
                    <div className="text-muted small">These details are filled automatically from the merchant application. The selected owner's email is also used as the agreement acceptor email.</div>
                  </div>
                  <Row className="g-3">
                    {decisionOwners.length > 1 && (
                      <Col md={4}>
                        <Form.Label>Owner Accepting Agreement</Form.Label>
                        <Form.Select value={selectedDecisionOwnerIndex} onChange={(e) => setSelectedDecisionOwnerIndex(Number(e.target.value))}>
                          {decisionOwners.map((owner, index) => (
                            <option key={`${owner.type || "Owner"}-${index}`} value={index}>
                              {owner.type || `Owner ${index + 1}`} · {owner.firstName} {owner.lastName}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                    )}
                    <Col md={4}>
                      <Form.Label>Owner First Name</Form.Label>
                      <Form.Control value={selectedDecisionOwner.firstName || ""} readOnly />
                    </Col>
                    <Col md={4}>
                      <Form.Label>Owner Last Name</Form.Label>
                      <Form.Control value={selectedDecisionOwner.lastName || ""} readOnly />
                    </Col>
                    <Col md={4}>
                      <Form.Label>Owner Email</Form.Label>
                      <Form.Control value={decisionOwnerEmail} readOnly />
                    </Col>
                    <Col md={4}>
                      <Form.Label>Owner SSN Last 4</Form.Label>
                      <Form.Control value={decisionOwnerSsn ? `•••• ${decisionOwnerSsn}` : ""} readOnly />
                    </Col>
                    <Col md={4}>
                      <Form.Label>Routing Number</Form.Label>
                      <Form.Control
                        placeholder="9-digit ABA routing number"
                        inputMode="numeric"
                        maxLength={9}
                        value={bankRoutingNumber}
                        onChange={(e) => setBankRoutingNumber(e.target.value.replace(/\D/g, "").slice(0, 9))}
                        isInvalid={Boolean(bankRoutingNumber) && !isValidAbaRoutingNumber(bankRoutingNumber)}
                      />
                      <Form.Control.Feedback type="invalid">Enter a valid 9-digit ABA routing number.</Form.Control.Feedback>
                    </Col>
                    <Col md={4}>
                      <Form.Label>Account Number</Form.Label>
                      <Form.Control
                        placeholder="8 to 17 digit account number"
                        inputMode="numeric"
                        maxLength={17}
                        value={bankAccountNumber}
                        onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 17))}
                        isInvalid={Boolean(bankAccountNumber) && !isValidBankAccountNumber(bankAccountNumber)}
                      />
                      <Form.Control.Feedback type="invalid">Enter 8 to 17 digits.</Form.Control.Feedback>
                    </Col>
                    <Col md={4}>
                      <Form.Label>Account Type</Form.Label>
                      <Form.Select value={bankAccountType} onChange={(e) => setBankAccountType(e.target.value)}>
                        <option value="Checking">Checking</option>
                        <option value="Savings">Savings</option>
                      </Form.Select>
                    </Col>
                    <Col md={12}>
                      <Form.Label>Comment</Form.Label>
                      <Form.Control placeholder="Example: Submitting decision." value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} />
                    </Col>
                  </Row>
                  <Button
                    className="mt-3"
                    onClick={submitMerchantDecision}
                    disabled={
                      loading ||
                      !applicationId ||
                      !ratesFees.length ||
                      !acceptanceLanguage.length ||
                      acceptanceLanguage.some(
                        (item: any) => item?.isConsentRequired && item?.propertyName !== "AcceptButton" && !acceptedConsents[item.propertyName]
                      ) ||
                      !selectedDecisionOwner.firstName ||
                      !selectedDecisionOwner.lastName ||
                      !decisionOwnerEmail ||
                      !decisionOwnerSsn ||
                      !isValidAbaRoutingNumber(bankRoutingNumber) ||
                      !isValidBankAccountNumber(bankAccountNumber)
                    }
                  >
                    <Send size={16} className="me-2" /> Accept & Submit
                  </Button>
                </Card.Body>
              </Card>
              <div className="merchant-acceptance-bar mt-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div>
                    <div className="fw-semibold">{acceptedConsentCount} of {requiredConsentItems.length} required agreements accepted</div>
                    <div className="text-muted small">Complete the required acknowledgements to submit.</div>
                  </div>
                  <Badge bg={consentProgress === 100 ? "success" : "primary"} pill>{consentProgress}%</Badge>
                </div>
                <div className="progress merchant-consent-progress mt-3" role="progressbar" aria-valuenow={consentProgress} aria-valuemin={0} aria-valuemax={100}>
                  <div className="progress-bar" style={{ width: `${consentProgress}%` }} />
                </div>
              </div>
          </div>
        )}

        {mode === "payments" && (
          <Card>
            <Card.Body>
              <SectionHeader title="Create Payment Link" subtitle="Generate a Deluxe hosted payment link for a homeowner invoice." />
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Merchant Account ID</Form.Label>
                  <Form.Control placeholder="Example: 1234567890123456" value={merchantAccountId} onChange={(e) => setMerchantAccountId(e.target.value)} />
                </Col>
                <Col md={4}>
                  <Form.Label>Invoice ID</Form.Label>
                  <Form.Control placeholder="Example: inv1234" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} />
                </Col>
                <Col md={4}>
                  <Form.Label>Payment Amount</Form.Label>
                  <Form.Control type="number" placeholder="Example: 19.99" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
                </Col>
                <Col md={4}>
                  <Form.Label>Homeowner First Name</Form.Label>
                  <Form.Control placeholder="Example: Jane" value={homeownerFirstName} onChange={(e) => setHomeownerFirstName(e.target.value)} />
                </Col>
                <Col md={4}>
                  <Form.Label>Homeowner Last Name</Form.Label>
                  <Form.Control placeholder="Example: Doe" value={homeownerLastName} onChange={(e) => setHomeownerLastName(e.target.value)} />
                </Col>
                <Col md={4}>
                  <Form.Label>Homeowner Email</Form.Label>
                  <Form.Control placeholder="Example: customer@test.com" value={homeownerEmail} onChange={(e) => setHomeownerEmail(e.target.value)} />
                </Col>
              </Row>
              <Button className="mt-3" onClick={createPaymentLink} disabled={loading || !merchantAccountId || !paymentAmount}>
                <CreditCard size={16} className="me-2" /> Create Link
              </Button>
              {paymentLink?.paymentLinkURL && (
                <Alert variant="success" className="mt-3">
                  <CheckCircle2 size={16} className="me-2" />
                  <a href={paymentLink.paymentLinkURL} target="_blank" rel="noreferrer">{paymentLink.paymentLinkURL}</a>
                </Alert>
              )}
            </Card.Body>
          </Card>
        )}

        {mode === "payouts" && (
          <Card>
            <Card.Body>
              <SectionHeader title="Payouts" subtitle="Settlement and payout APIs are not part of the provided Deluxe docs yet." />
              <Alert variant="warning" className="mb-0">
                <FileText size={16} className="me-2" />
              Coming soon
              </Alert>
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
}
